import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

/**
 * Presents product recommendations on the product page as two tabs.
 *
 * This component owns the tabs and the "You also viewed" panel only. The "Similar items"
 * panel is either rendered by the server (when the curated metafield is populated) or filled
 * by the theme's existing `<product-recommendations>` element, which hydrates itself with
 * morphSection in 'hydration' mode. That mode only morphs nodes carrying a matching
 * `data-hydration-key`, so the tablist is never touched by it.
 *
 * A tab whose panel resolves to nothing is removed rather than shown empty. When a single tab
 * is left the tablist stops being drawn, since there is no longer a choice to make.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [tablist] - The row of tabs.
 * @property {HTMLElement} [similarPanel] - The panel holding the similar items.
 * @property {HTMLElement} [viewedPanel] - The panel holding the previously viewed items.
 * @property {HTMLElement} [viewedContent] - The swappable contents of the viewed panel.
 *
 * @extends Component<Refs>
 */
class ProductRecommendationsTabs extends Component {
  /** @type {number} */
  #activeIndex = 0;

  /**
   * Set once the viewed panel has been settled, successfully or not, so a second intersection
   * never fires a second request.
   * @type {boolean}
   */
  #viewedResolved = false;

  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#fetchViewedProducts();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * The similar panel is filled by another element on its own schedule, so its outcome is
   * watched rather than awaited.
   * @type {MutationObserver}
   */
  #similarObserver = new MutationObserver(() => this.#syncTabs());

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tab"]')));
  }

  /** @returns {HTMLElement[]} */
  get #panels() {
    return /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[role="tabpanel"]')));
  }

  connectedCallback() {
    super.connectedCallback();

    // Whether there is anything to show is decided now rather than on scroll: reading
    // localStorage costs nothing, and a tab that appears only to vanish a moment later is
    // worse than one that was never there. Only the request itself waits for the viewport.
    if (this.#viewedIds.length) {
      this.#intersectionObserver.observe(this);
    } else {
      this.#resolveViewed(null);
    }

    const { similarPanel } = this.refs;
    if (similarPanel) {
      // Only the attributes that say something about the outcome are watched. Watching every
      // attribute would include the panel's own `hidden`, which this component sets, and each
      // pass would wake the observer that triggered it.
      this.#similarObserver.observe(similarPanel, {
        attributeFilter: ['data-recommendations-performed', 'data-error', 'class'],
        childList: true,
        subtree: true,
      });
    }

    this.#activate(this.#activeIndex, false);
    this.#syncTabs();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#similarObserver.disconnect();
  }

  /**
   * @param {Event} event - The click event.
   */
  selectTab(event) {
    const { target } = event;
    if (!(target instanceof Element)) return;

    const tab = target.closest('[role="tab"]');
    if (!(tab instanceof HTMLElement)) return;

    this.#activate(this.#tabs.indexOf(tab), false);
  }

  /**
   * Arrow keys move between tabs, as expected of a tablist.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    const count = this.#tabs.length;
    if (!count) return;

    let next = this.#activeIndex;

    switch (event.key) {
      case 'ArrowRight':
        next = (this.#activeIndex + 1) % count;
        break;
      case 'ArrowLeft':
        next = (this.#activeIndex - 1 + count) % count;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = count - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next, true);
  }

  /**
   * @param {number} index - Index of the tab to activate.
   * @param {boolean} moveFocus - Whether to focus the tab, which suits the keyboard but not a click.
   */
  #activate(index, moveFocus) {
    const tabs = this.#tabs;
    const panels = this.#panels;
    if (!tabs.length) return;

    // Tabs are removed when their panel turns out to be empty, so the stored index is clamped
    // rather than trusted.
    const active = Math.min(Math.max(index, 0), tabs.length - 1);
    this.#activeIndex = active;

    tabs.forEach((tab, position) => {
      const isSelected = position === active;

      tab.setAttribute('aria-selected', String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;

      if (isSelected && moveFocus) tab.focus();
    });

    panels.forEach((panel, position) => panel.toggleAttribute('hidden', position !== active));
  }

  /**
   * The products already seen, most recent first, minus the one on screen.
   *
   * scripts.liquid records the current product on every product page, so it is always the
   * first entry and always the one dropped here.
   *
   * @returns {string[]}
   */
  get #viewedIds() {
    const { productId, maxProducts } = this.dataset;

    return RecentlyViewed.getProducts()
      .filter((/** @type {string} */ id) => id !== productId)
      .slice(0, Number(maxProducts) || 4);
  }

  /**
   * The dynamic section id of the renderer, which lives in the search template.
   *
   * A section inside a JSON template does not answer to its key: Shopify assigns it
   * `template--<n>__<key>`, and the bare key returns 404. Liquid can only hand out
   * `section.id` for the section being rendered, never for one in another template, so the
   * prefix is borrowed from this section's own wrapper. That number identifies the theme, not
   * the template, so the same prefix addresses the search template's sections.
   *
   * @returns {string | null}
   */
  get #viewedSectionId() {
    const key = this.dataset.viewedSectionKey;
    if (!key) return null;

    const ownId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '') ?? '';
    const separator = ownId.indexOf('__');

    // Rendered outside a JSON template there is no prefix to borrow, and the key alone would
    // only 404, so there is nothing useful to ask for.
    if (separator === -1) return null;

    return ownId.slice(0, separator + 2) + key;
  }

  /**
   * Asks the search route for the markup of the viewed products. Liquid cannot resolve a
   * product by id, so the search index is the only way in — the same route the search drawer
   * uses for its own recently viewed row.
   */
  async #fetchViewedProducts() {
    if (this.#viewedResolved) return;

    const { searchUrl } = this.dataset;
    const sectionId = this.#viewedSectionId;
    const ids = this.#viewedIds;

    if (!ids.length || !sectionId || !searchUrl) {
      this.#resolveViewed(null);
      return;
    }

    try {
      const url = new URL(searchUrl, location.origin);
      url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');

      const markup = await sectionRenderer.getSectionHTML(sectionId, false, url);
      const list = new DOMParser()
        .parseFromString(markup, 'text/html')
        .getElementById('recently-viewed-products-list');

      this.#resolveViewed(list);
    } catch (error) {
      // A failed lookup is not worth breaking the page over: the tab simply does not appear.
      // The theme editor renders sections in contexts the Section Rendering API cannot always
      // resolve, which is noise rather than a real fault.
      if (!Shopify.designMode) {
        console.error('Recently viewed products error:', error instanceof Error ? error.message : error);
      }
      this.#resolveViewed(null);
    }
  }

  /**
   * The theme editor almost always has an empty history, so pruning empty tabs there would
   * leave the merchant looking at a bare carousel with no way to see, select or style the
   * tabs they just added. In the editor the structure stays put; on the storefront it does not.
   *
   * @returns {boolean}
   */
  get #preserveAllTabs() {
    return Boolean(Shopify.designMode);
  }

  /**
   * @param {HTMLElement | null} list - The rendered list, or null when there is nothing to show.
   */
  #resolveViewed(list) {
    this.#viewedResolved = true;

    const { viewedContent } = this.refs;

    // The skeleton is swapped wholesale rather than morphed: there is no state in a
    // placeholder worth preserving, and the cards arrive as a fresh subtree.
    if (list && viewedContent) viewedContent.replaceChildren(list);
    if (!list && !this.#preserveAllTabs) this.#removeTab('viewed');

    this.#syncTabs();
  }

  /**
   * Whether the similar panel is still working, has cards, or came back with nothing.
   * @returns {'pending' | 'filled' | 'empty'}
   */
  get #similarState() {
    const { similarPanel } = this.refs;
    if (!similarPanel) return 'empty';

    const recommendations = similarPanel.querySelector('product-recommendations');

    // No element at all means the curated metafield filled the panel server side.
    if (!(recommendations instanceof HTMLElement)) {
      return similarPanel.querySelector('.resource-list__item') ? 'filled' : 'empty';
    }

    if (recommendations.dataset.error) return 'empty';
    if (recommendations.dataset.recommendationsPerformed !== 'true') return 'pending';

    return recommendations.querySelector('.resource-list__item') ? 'filled' : 'empty';
  }

  /**
   * @param {string} name - The `data-tab` value of the tab to drop.
   */
  #removeTab(name) {
    const tab = this.querySelector(`[role="tab"][data-tab="${name}"]`);
    const panel = this.querySelector(`[role="tabpanel"][data-panel="${name}"]`);

    tab?.remove();
    panel?.remove();
  }

  /**
   * Keeps the tablist honest: no tab without content, no tablist without a choice, and no
   * module at all when neither panel has anything to show.
   */
  #syncTabs() {
    if (this.#similarState === 'empty' && !this.#preserveAllTabs) {
      this.#removeTab('similar');
      this.#similarObserver.disconnect();
    }

    const tabs = this.#tabs;

    if (!tabs.length) {
      // In the theme editor the section has to stay selectable, otherwise the merchant cannot
      // reach its settings to fix the very thing that left it empty.
      if (!Shopify.designMode) this.toggleAttribute('hidden', true);
      return;
    }

    // A lone tab is kept on screen rather than hidden: it is the only heading the module has,
    // and a carousel with nothing named above it reads as a stray row of products.
    this.toggleAttribute('hidden', false);

    // Safe to paint the row now. The viewed tab's fate is decided synchronously on connect,
    // so this runs before the first frame the shopper sees rather than after it.
    this.refs.tablist?.setAttribute('data-resolved', 'true');

    // Removing a tab can leave the stored index pointing past the end, or leave every panel
    // hidden when the removed one was the selected tab. Falling back to the first tab keeps a
    // panel on screen either way.
    const selected = tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#activate(selected === -1 ? 0 : selected, false);
  }
}

if (!customElements.get('product-recommendations-tabs')) {
  customElements.define('product-recommendations-tabs', ProductRecommendationsTabs);
}
